import { useMsal } from '@azure/msal-react';
import { Button, Text, Tooltip } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useStores, useTitle } from '../../common/hooks';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import { NominationRouteParams } from '../../models/route-params';
import { RoutePaths } from '../../route-paths';
import Loading from '../shared-components/Loading';
import { FlexBox } from '../shared-components/flex-box';
import './ReviewAndSubmitNominationPage.scss';
import { AdditionalContactSection } from './components/AdditionalContactSection';
import { CompanyInformationSection } from './components/CompanyInformationSection';
import { ConfirmReviewAndSubmitModal } from './components/ConfirmReviewAndSubmitModal';
import { CustomerReferencesSection } from './components/CustomerReferencesSection';
import { JointNomineesSection } from './components/JointNomineesSection';
import { NoCountrySelectionModal } from './components/NoCountrySelectionModal';
import { NominationNameSection } from './components/NominationNameSection';
import { NominationSubmissionSection } from './components/NominationSubmissionSection';

interface ReviewAndSubmitProps {
  onSubmitNominationSuccess(): void;
}

const ReviewAndSubmitNominationPageImpl: React.FC<ReviewAndSubmitProps> = (props) => {
  const {
    onSubmitNominationSuccess,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    reviewAndSubmitNominationPageUiStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { nominationId } = useParams<NominationRouteParams>();
  const navigate = useNavigate();

  const getNominationById = async (nominationId: number): Promise<void> => {
    try {
      const nomination = await apiStore.getNominationById(instance, nominationId);
      reviewAndSubmitNominationPageUiStore.setCurrentNomination(nomination);
      await getAwardById(nomination.award.awardId);
    } catch (error) {
      // TODO - Handle Error
    }
  };

  const getAwardById = async (awardId: number): Promise<void> => {
    try {
      const award = await apiStore.getAwardById(instance, awardId);
      reviewAndSubmitNominationPageUiStore.setCurrentAward(award);
    } catch (error) {
      // TODO - handle error
    }
  }

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (!nominationId) {
      setIsLoading(false);
      return;
    }

    const currentNominationId = Number(nominationId);

    await getNominationById(currentNominationId);

    setIsLoading(false);
  };

  const handleSubmitNomination = (): void => {
    if (!reviewAndSubmitNominationPageUiStore.currentNomination) return;

    const isValidCountrySelection = reviewAndSubmitNominationPageUiStore.isCountryAward ?
      reviewAndSubmitNominationPageUiStore.hasAssociatedCountries :
      reviewAndSubmitNominationPageUiStore.onlySentinelCountry;

    if (!isValidCountrySelection) {
      reviewAndSubmitNominationPageUiStore.setShowNoCountrySelectionModal(true);
      return;
    }

    reviewAndSubmitNominationPageUiStore.setShowConfirmationModal(true);
  };

  const handleDismissNoCountrySelectionModal = (): void => {
    reviewAndSubmitNominationPageUiStore.setShowNoCountrySelectionModal(false);
  };

  const handleDismissConfirmReviewAndSubmitModal = (): void => {
    reviewAndSubmitNominationPageUiStore.setShowConfirmationModal(false);
  };

  const handleConfirmSubmitNomination = async (): Promise<void> => {
    if (!reviewAndSubmitNominationPageUiStore.currentNomination) return;

    setIsLoading(true);

    try {
      await apiStore.updateNominationStatus(
        instance,
        reviewAndSubmitNominationPageUiStore.currentNomination.nominationId,
        {
          status: 'Submitted',
        },
      );

      onSubmitNominationSuccess();
      navigate(RoutePaths.Home, { replace: true });
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  useTitle('Nomination Process - Review and Submit');
  useEffect(() => {
    preloadData();
    return () => {
      reviewAndSubmitNominationPageUiStore.resetStates();
    };
  }, []);

  return (
    <FlexBox
      className='review-and-submit-nomination-page'
    >
      {isLoading && <Loading />}
      {
        reviewAndSubmitNominationPageUiStore.currentNomination && (
          <>
            <aside>
              <Text
                as='h1'
                weight='semibold'
                size={400}
                block
              >
                Review and submit
              </Text>
              <Text
                block
                as='p'
              >
                Click Submit to finalize your nomination. Note that you will not be able to edit your nomination once it is submitted. Please make sure you have reviewed for any typos or grammatical errors before finalizing. To cancel and return to editing, click Back.
              </Text>
            </aside>

            <div className='review-and-submit-nomination-page__nomination'>
              <FlexBox
                as='section'
                isFullWidth
                content='space-between'
                className='nomination-header'
              >
                <Text
                  as='h2'
                  weight='semibold'
                  size={500}
                  block
                >
                  {reviewAndSubmitNominationPageUiStore.currentNomination.award.title}
                </Text>

                <FlexBox
                  aligns='center'
                  className='nomination-header__actions'
                >
                  <NavLink
                    to={`${RoutePaths.Nominations}/${reviewAndSubmitNominationPageUiStore.currentNomination.nominationId}`}
                    className='secondary-btn'
                    end
                  >
                    Back
                  </NavLink>
                  {
                    reviewAndSubmitNominationPageUiStore.currentNomination.status === 'Submitted' ? (
                      <Tooltip
                        content={reviewAndSubmitNominationPageUiStore.currentNomination.status === 'Submitted' ? getTooltipContentByFieldName('review-and-submit', 'reviewAndSubmitButton') ?? '' : ''}
                        relationship='description'
                      >
                        <Button
                          appearance='primary'
                          disabled
                        >
                          Save changes
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        appearance='primary'
                        onClick={handleSubmitNomination}
                      >
                        Submit
                      </Button>
                    )
                  }
                </FlexBox>
              </FlexBox>
              <NominationNameSection />
              <CompanyInformationSection />
              <AdditionalContactSection />
              <JointNomineesSection />
              <NominationSubmissionSection />
              {
                reviewAndSubmitNominationPageUiStore.currentNomination.award.requireCustomerReference &&
                <CustomerReferencesSection />
              }
            </div>
            <NoCountrySelectionModal
              isModalOpen={reviewAndSubmitNominationPageUiStore.isShowNoCountrySelectionModal}
              onDismissModal={handleDismissNoCountrySelectionModal}
            />
            <ConfirmReviewAndSubmitModal
              isModalOpen={reviewAndSubmitNominationPageUiStore.isShowConfirmationModal}
              onDismissModal={handleDismissConfirmReviewAndSubmitModal}
              onSubmitModal={handleConfirmSubmitNomination}
            />
          </>
        )
      }
      {
        !reviewAndSubmitNominationPageUiStore.currentNomination && (
          <Text
            as='p'
            className='page-fallback'
          >
            Sorry, no nomination is currently selected.
          </Text>
        )
      }
    </FlexBox>
  )
}

export const ReviewAndSubmitNominationPage = observer(ReviewAndSubmitNominationPageImpl);
